import type {
  CentinelaGeofenceCorridor,
  CentinelaGeofenceSource,
  CentinelaGeofenceSuggestion,
  CentinelaGeofenceSuggestionsJob,
} from "../../../features/centinela/types";
import { typeColors } from "../../admin/components/sections/geofences/utils/helpers";
import {
  GEOFENCE_SUGGESTION_CONFIDENCES,
  type GeofenceSuggestion,
  type GeofenceSuggestionConfidence,
  type GeofenceSuggestionSource,
} from "../types";

export const GEOFENCE_SUGGESTION_JOB_STATES = [
  "running",
  "ready",
  "failed",
  "unknown",
] as const;

export type GeofenceSuggestionJobState =
  (typeof GEOFENCE_SUGGESTION_JOB_STATES)[number];

export interface GeofenceSuggestionCorridor {
  distanceKm: number | null;
  samples: number | null;
}

export interface GeofenceSuggestionAnalysis {
  state: GeofenceSuggestionJobState;
  stateRaw: string | null;
  isRunning: boolean;
  isReady: boolean;
  hasError: boolean;
  errorMessage: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  corridor: GeofenceSuggestionCorridor | null;
  suggestions: GeofenceSuggestion[];
  sources: GeofenceSuggestionSource[];
  searches: number | null;
  discardedCount: number;
  costUsd: number | null;
}

const READY_STATE = "listo";
const RUNNING_STATE = "corriendo";

const ERROR_KEYS = ["mensaje", "message", "detalle", "error"] as const;

const toText = (value: unknown): string | null => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const toNumber = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

const normalizeKey = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const isConfidence = (value: string): value is GeofenceSuggestionConfidence =>
  (GEOFENCE_SUGGESTION_CONFIDENCES as readonly string[]).includes(value);

const toErrorMessage = (error: unknown): string | null => {
  const text = toText(error);
  if (text) return text;
  if (!error || typeof error !== "object") return null;

  const record = error as Record<string, unknown>;
  const found = ERROR_KEYS.map((key) => toText(record[key])).find(Boolean);
  return found ?? JSON.stringify(error);
};

const toJobState = (
  estado: string | null,
  hasError: boolean,
): GeofenceSuggestionJobState => {
  if (hasError) return "failed";
  if (!estado) return "unknown";

  const key = normalizeKey(estado);
  if (key === READY_STATE) return "ready";
  if (key === RUNNING_STATE) return "running";
  return "unknown";
};

export const mapCentinelaGeofenceCorridor = (
  corridor: CentinelaGeofenceCorridor | null | undefined,
): GeofenceSuggestionCorridor | null => {
  if (!corridor) return null;

  const distanceKm = toNumber(corridor.distancia_km);
  const samples = toNumber(corridor.muestras);
  if (distanceKm === null && samples === null) return null;

  return { distanceKm, samples };
};

const toSources = (
  sources: CentinelaGeofenceSource[] | null | undefined,
): GeofenceSuggestionSource[] => {
  const seen = new Set<string>();

  return (sources ?? []).reduce<GeofenceSuggestionSource[]>((acc, source) => {
    const url = toText(source?.url);
    if (!url || seen.has(url)) return acc;
    seen.add(url);
    return [...acc, { url, title: toText(source?.titulo) ?? url }];
  }, []);
};

const colorOf = (type: string | null) =>
  (type && typeColors[type]) || typeColors.default;

const toSuggestion = (
  suggestion: CentinelaGeofenceSuggestion,
  index: number,
  jobId: string,
): GeofenceSuggestion => {
  const type = toText(suggestion?.tipo);
  const confidenceRaw = toText(suggestion?.confianza);
  const normalized = confidenceRaw ? normalizeKey(confidenceRaw) : null;

  return {
    id: `${jobId}-${index}`,
    name: toText(suggestion?.nombre) ?? "",
    reason: toText(suggestion?.motivo) ?? "",
    type: type ?? "",
    color: colorOf(type),
    origin: toText(suggestion?.origen),
    confidence: normalized && isConfidence(normalized) ? normalized : null,
    confidenceRaw,
    sources: toSources(suggestion?.fuentes),
    distanceToRouteKm: toNumber(suggestion?.distancia_a_ruta_km),
    lat: toNumber(suggestion?.centro?.latitude),
    lng: toNumber(suggestion?.centro?.longitude),
    radiusMeters: toNumber(suggestion?.radio_m),
  };
};

export function mapCentinelaGeofenceSuggestions(
  job: CentinelaGeofenceSuggestionsJob | undefined,
): GeofenceSuggestionAnalysis | null {
  if (!job) return null;

  const errorMessage = toErrorMessage(job.error);
  const stateRaw = toText(job.estado);
  const state = toJobState(stateRaw, Boolean(errorMessage));
  const result = job.resultado ?? null;
  const jobId = toText(job.id) ?? "job";

  return {
    state,
    stateRaw,
    isRunning: state === "running" || state === "unknown",
    isReady: state === "ready",
    hasError: state === "failed",
    errorMessage,
    startedAt: toText(job.iniciado_en),
    finishedAt: toText(job.terminado_en),
    corridor: mapCentinelaGeofenceCorridor(result?.corredor),
    suggestions:
      state === "ready"
        ? (result?.sugerencias ?? []).map((suggestion, index) =>
            toSuggestion(suggestion, index, jobId),
          )
        : [],
    sources: toSources(result?.investigacion?.fuentes),
    searches: toNumber(result?.investigacion?.busquedas),
    discardedCount: (result?.descartadas ?? []).length,
    costUsd: toNumber(result?.costo?.usd),
  };
}
